/**
 * MasteryBadgePanel
 *
 * Displays per-skill mastery tiers as colored badges and an optional
 * "Review due" count badge drawn from the adaptive review queue.
 *
 * Tier → color mapping (Feenix palette):
 *   cold_start / emerging   → red   (#ef4444)
 *   developing              → amber (#f59e0b)
 *   proficient              → green (#22c55e)
 *   mastered                → teal  (#14b8a6)
 *
 * Props:
 *   learnerId  — string
 *   compact    — boolean (true → abbreviated badge-strip, false → full table)
 */

import React, { useEffect, useState } from 'react';
import { adaptiveAPI } from '../../services/api';

const TIER_CONFIG = {
  cold_start:  { label: 'Emerging',   color: '#ef4444', bg: 'rgba(239,68,68,0.14)'  },
  emerging:    { label: 'Emerging',   color: '#ef4444', bg: 'rgba(239,68,68,0.14)'  },
  developing:  { label: 'Developing', color: '#f59e0b', bg: 'rgba(245,158,11,0.14)' },
  proficient:  { label: 'Proficient', color: '#22c55e', bg: 'rgba(34,197,94,0.14)'  },
  mastered:    { label: 'Mastered',   color: '#14b8a6', bg: 'rgba(20,184,166,0.14)' },
};

const DEFAULT_TIER = TIER_CONFIG.cold_start;

// Expand known domain prefixes and week-number patterns into readable names.
// e.g.  ai_orch_w1  →  "AI Orchestrator - Week 1"
//       lsat_rc     →  "LSAT - Rc"
const SKILL_PREFIX_MAP = [
  ['ai_orchestrator', 'AI Orchestrator'],
  ['ai_entrepreneur', 'AI Entrepreneur'],
  ['ai_beginner',     'AI Beginner'],
  ['ai_orch',         'AI Orchestrator'],
  ['ai_entr',         'AI Entrepreneur'],
  ['ai_beg',          'AI Beginner'],
  ['football',        'Football IQ'],
  ['lsat',            'LSAT'],
  ['sat',             'SAT'],
];

function formatSkillId(id) {
  if (!id) return id || '';
  const lower = id.toLowerCase();
  for (const [prefix, label] of SKILL_PREFIX_MAP) {
    if (lower.startsWith(prefix)) {
      const rest = id.slice(prefix.length).replace(/^[_-]/, '');
      if (!rest) return label;
      // Format week patterns: w1 → Week 1
      const restFormatted = rest
        .replace(/\bw(\d+)\b/gi, 'Week $1')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
      return `${label} - ${restFormatted}`;
    }
  }
  // Generic fallback: snake_case → Title Case, expand week patterns
  return id
    .replace(/\bw(\d+)\b/gi, 'Week $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function tierConfig(tier) {
  return TIER_CONFIG[tier?.toLowerCase()] || DEFAULT_TIER;
}

function SkillBadge({ skillId, skillLabel, tier }) {
  const cfg = tierConfig(tier);
  // Use the best available label; fall back to formatting the raw ID
  const displayLabel = skillLabel && skillLabel !== skillId
    ? skillLabel
    : formatSkillId(skillId);
  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '8px 12px',
        borderRadius: 10,
        background: cfg.bg,
        border: `1px solid ${cfg.color}55`,
        minWidth: 90,
      }}
      title={`${displayLabel}: ${cfg.label}`}
    >
      <span style={{ fontSize: 11, fontWeight: 700, color: cfg.color, letterSpacing: 0.5 }}>
        {cfg.label}
      </span>
      <span style={{ fontSize: 11, color: '#cbd5e1', maxWidth: 100, textAlign: 'center', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
        {displayLabel}
      </span>
    </div>
  );
}

export default function MasteryBadgePanel({ learnerId, compact = false, onStartReview }) {
  const [mastery, setMastery] = useState(null);
  const [reviewQueue, setReviewQueue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!learnerId) return;
    let cancelled = false;

    Promise.all([
      adaptiveAPI.getMastery(learnerId),
      adaptiveAPI.getReviewQueue(learnerId),
    ])
      .then(([masteryRes, reviewRes]) => {
        if (cancelled) return;
        setMastery(masteryRes.data);
        setReviewQueue(reviewRes.data);
      })
      .catch((e) => {
        if (!cancelled) setError('Could not load mastery data.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [learnerId]);

  if (loading) {
    return (
      <div style={panelStyle}>
        <div style={panelHeaderStyle}>
          <h3 style={panelTitleStyle}>Skill Mastery</h3>
        </div>
        <div style={{ color: '#64748b', fontSize: 13, padding: '12px 0' }}>Loading mastery…</div>
      </div>
    );
  }

  if (error) {
    return null; // non-blocking — mastery panel is bonus info
  }

  const skills = mastery?.skills || mastery || [];
  const dueCount = reviewQueue?.total ?? reviewQueue?.due_count ?? reviewQueue?.count ?? 0;

  if (!Array.isArray(skills) || skills.length === 0) {
    return (
      <div style={panelStyle}>
        <div style={panelHeaderStyle}>
          <h3 style={panelTitleStyle}>Skill Mastery</h3>
        </div>
        <p style={{ color: '#64748b', fontSize: 13 }}>
          Complete your first session to start tracking mastery.
        </p>
      </div>
    );
  }

  // Group skills by domain if domain_id is present
  const domains = {};
  for (const s of skills) {
    const d = s.domain_id || s.domain || 'General';
    if (!domains[d]) domains[d] = [];
    domains[d].push(s);
  }

  return (
    <div style={panelStyle}>
      <div style={panelHeaderStyle}>
        <div>
          <h3 style={panelTitleStyle}>Skills Mastered</h3>
          <p style={{ margin: 0, fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
            Individual skill proficiency tracked per session
          </p>
        </div>
          {dueCount > 0 && (
          <div
            style={{
              ...reviewBadgeStyle,
              cursor: onStartReview ? 'pointer' : 'default',
              userSelect: 'none',
            }}
            role={onStartReview ? 'button' : undefined}
            tabIndex={onStartReview ? 0 : undefined}
            aria-label={`${dueCount} flashcard review${dueCount !== 1 ? 's' : ''} due — click to start`}
            onClick={() => onStartReview && onStartReview()}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onStartReview && onStartReview()}
          >
            <span style={{ fontWeight: 700 }}>{dueCount}</span>
            &nbsp;review{dueCount !== 1 ? 's' : ''} due
            {onStartReview && (
              <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.8 }}>▶ Start</span>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={legendRowStyle}>
        {Object.values(TIER_CONFIG)
          .filter((c, i, arr) => arr.findIndex((x) => x.label === c.label) === i)
          .map((cfg) => (
            <span key={cfg.label} style={legendItemStyle}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.color, display: 'inline-block' }} />
              {cfg.label}
            </span>
          ))}
      </div>

      {/* Skills grouped by domain */}
      {Object.entries(domains).map(([domainName, domainSkills]) => (
        <div key={domainName} style={{ marginBottom: 16 }}>
          <div style={domainLabelStyle}>{domainName.replace(/_/g, ' ')}</div>
          <div style={badgeGridStyle}>
            {domainSkills.map((s) => (
              <SkillBadge
                key={s.skill_id || s.id}
                skillId={s.skill_id || s.id}
                skillLabel={s.display_name || s.skill_label || s.label || s.name || null}
                tier={s.tier || s.mastery_tier}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const panelStyle = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 12,
  padding: '20px 24px',
  marginBottom: 24,
};

const panelHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 12,
};

const panelTitleStyle = {
  fontSize: 16,
  fontWeight: 700,
  margin: 0,
  color: 'var(--text-primary, #f8fafc)',
};

const reviewBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 12px',
  background: 'rgba(245,158,11,0.15)',
  border: '1px solid rgba(245,158,11,0.4)',
  borderRadius: 999,
  fontSize: 12,
  color: '#fbbf24',
  fontWeight: 600,
};

const legendRowStyle = {
  display: 'flex',
  gap: 14,
  marginBottom: 16,
  flexWrap: 'wrap',
};

const legendItemStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  fontSize: 11,
  color: '#94a3b8',
};

const domainLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1,
  textTransform: 'uppercase',
  color: '#64748b',
  marginBottom: 8,
};

const badgeGridStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
};
